import { STATUS } from "@/constants/statuses";
import { customerInfoProviderToken } from "@/tokens";
import { applyTransaction, arrayAdd } from "@datorama/akita";
import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
import { ProductsService } from "@modules/products/services/ProductsService";
import { Observable } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { inject, singleton } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import { ShareWishlistsApiService } from "../api_services/ShareWishlistsApiService";
import { WishlistsApiService } from "../api_services/WishlistsApiService/WishlistsApiService";
import { WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES } from "../constants/constants";
import { ShareWishlistsResultEntity } from "../entities/ShareWishlistsResultEntity";
import { WishlistEntity } from "../entities/WishlistEntity";
import ShareWishlistsStore from "../stores/ShareWishlistsStore";
import { wishlistApplicationLoggerToken } from "../tokens";
import { QueryPaginationMeta } from "../types";

@singleton()
export class ShareWishlistsService {
    constructor(
        private readonly shareWishlistsStore: ShareWishlistsStore,
        private readonly shareWishlistsApiService: ShareWishlistsApiService,
        private readonly wishlistsApiService: WishlistsApiService,
        private readonly productsService: ProductsService,
        @inject(wishlistApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        @inject(customerInfoProviderToken)
        private readonly customerInfoProvider: CustomerInfoProviderInterface
    ) {}

    shareWishlists(
        channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
        wishlistIds: number[]
    ): Observable<ShareWishlistsResultEntity> {
        this.logger.debug("ShareWishlistsService.shareWishlists is started", {
            channelName,
            wishlistIds,
        });
        this.shareWishlistsStore.setStatus(STATUS.LOADING);
        return this.shareWishlistsApiService
            .shareWishlists(channelName, wishlistIds)
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "ShareWishlistsService.shareWishlists is errored",
                        { error }
                    );
                    this.shareWishlistsStore.setError(true);
                    throw error;
                }),
                tap(() => {
                    this.logger.debug(
                        "ShareWishlistsService.shareWishlists is ready",
                        { channelName, wishlistIds }
                    );
                    this.shareWishlistsStore.setStatus(STATUS.READY);
                })
            );
    }

    getSharedWishlists(shareToken: string): Observable<WishlistEntity[]> {
        this.logger.debug(
            "ShareWishlistsService.getSharedWishlists is started",
            { shareToken }
        );
        this.shareWishlistsStore.setStatus(STATUS.LOADING);
        return this.shareWishlistsApiService
            .getSharedWishlists(shareToken)
            .pipe(
                catchError((error: unknown) => {
                    this.logger.debug(
                        "ShareWishlistsService.getSharedWishlists is errored",
                        { error }
                    );
                    this.shareWishlistsStore.setError(true);
                    throw error;
                }),
                tap((wishlists) => {
                    wishlists.forEach((wishlist) => {
                        wishlist.items.forEach((wishlistItem) => {
                            this.productsService.addProductForFetching(
                                wishlistItem.productId
                            );

                            if (this.customerInfoProvider.getIsB2b()) {
                                this.productsService.addProductWithQuantityRulesForFetching(
                                    wishlistItem.productId
                                );
                            }
                        });
                    });
                }),
                tap((wishlists) => {
                    this.logger.debug(
                        "ShareWishlistsService.getSharedWishlists is ready",
                        { shareToken }
                    );
                    applyTransaction(() => {
                        this.shareWishlistsStore.add(wishlists);
                        this.shareWishlistsStore.setStatus(STATUS.READY);
                    });
                })
            );
    }

    getWishlistItems(queryParams: Required<QueryPaginationMeta>) {
        this.logger.debug("ShareWishlistsService.getWishlistItems is started");
        this.shareWishlistsStore.setLoadMoreStatus(STATUS.LOADING);

        return this.wishlistsApiService.getWishlistItems(queryParams).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "ShareWishlistsService.getWishlistItems is errored",
                    { error }
                );
                applyTransaction(() => {
                    this.shareWishlistsStore.setError(error);
                    this.shareWishlistsStore.setLoadMoreStatus(STATUS.ERROR);
                });
                throw error;
            }),
            tap((data) => {
                data.items.forEach((item) => {
                    this.productsService.addProductForFetching(item.productId);
                });
            }),
            tap((data) => {
                this.logger.debug(
                    "ShareWishlistsService.getWishlistItems is ready",
                    { data }
                );

                applyTransaction(() => {
                    this.shareWishlistsStore.update(
                        queryParams.wishlistId,
                        ({ items }) => ({
                            items: arrayAdd(items, data.items),
                            totalCount: data.totalCount,
                            currentOffset: data.currentOffset,
                        })
                    );
                    this.shareWishlistsStore.setLoadMoreStatus(STATUS.READY);
                });
            })
        );
    }
}
